import React, { FC, useEffect } from 'react';
import { Button, Form, FormItem, Header, Input, ScrollView, Select } from '$uikit';
import { useDispatch, useSelector } from 'react-redux';
import {
  isLoadingPersonalSubscriptionPriceSelector,
  personalSubscriptionPriceSelector,
  trialLinksActions,
  trialLinksFormSelector,
} from '$store/trialLinks';
import { Loader } from '$shared/components';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
export const CreateTrialLink: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { name, limit, expirationDays, periodDays } = useSelector(trialLinksFormSelector);
  const price = useSelector(personalSubscriptionPriceSelector);
  const isLoading = useSelector(isLoadingPersonalSubscriptionPriceSelector);

  useEffect(() => {
    dispatch(trialLinksActions.loadPersonalSubscriptionPrice());
    return () => {
      dispatch(trialLinksActions.clearForm());
    };
  }, []);

  const handleChangeName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    dispatch(
      trialLinksActions.setFormField({
        name: 'name',
        value: e.target.value,
      }),
    );
  };

  const handleChangeLimit = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      trialLinksActions.setFormField({
        name: 'limit',
        value: parseInt(e.target.value),
      }),
    );
  };

  const handleChangeExpirationDays = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      trialLinksActions.setFormField({
        name: 'expirationDays',
        value: parseInt(e.target.value),
      }),
    );
  };

  const handleChangePeriodDays = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      trialLinksActions.setFormField({
        name: 'periodDays',
        value: parseInt(e.target.value),
      }),
    );
  };

  const handleSubmit = () => {
    if (name.length > 64) {
      dispatch(toastActions.setToastFail(t('trial_links_toast_name_length_error')));
      return;
    }
    dispatch(trialLinksActions.createTrialLink());
  };

  return (
    <>
      <Header showCloseButton>{t('trial_link_form_title')}</Header>
      <ScrollView
        footer={<Button onClick={handleSubmit}>{t('trial_link_form_button')}</Button>}
      >
        {!isLoading ? (
          <Form>
            <FormItem
              title={t('trial_link_form_name')}
              description={`${name.length}/64`}
              descriptionAlign="right"
            >
              <Input
                value={name}
                onChange={handleChangeName}
                placeholder={t('trial_link_form_name_placeholder')}
              />
            </FormItem>
            <FormItem title={t('trial_link_form_offer_limit')}>
              <Select value={limit} onChange={handleChangeLimit}>
                <option value={0}>
                  {t('trial_link_form_offer_limit_without_limit')}
                </option>
                {Array.from({ length: 10 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    {t('trial_link_offer_limit_option', { count: i + 1 })}
                  </option>
                ))}
                <option value={50}>
                  {t('trial_link_offer_limit_option', { count: 50 })}
                </option>
                <option value={100}>
                  {t('trial_link_offer_limit_option', { count: 100 })}
                </option>
              </Select>
            </FormItem>
            <FormItem title={t('trial_link_expiration')}>
              <Select value={expirationDays} onChange={handleChangeExpirationDays}>
                <option value={0}>{t('trial_link_expiration_without_limit')}</option>
                {Array.from({ length: 30 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    {t('trial_link_expiration_option', { count: i + 1 })}
                  </option>
                ))}
              </Select>
            </FormItem>
            <FormItem
              title={t('trial_link_form_trial_duration')}
              description={t(
                periodDays
                  ? 'trial_link_form_trial_duration_desc'
                  : 'trial_link_form_trial_duration_without_limit',
                {
                  count: periodDays,
                  price: formatCurrency(price, CurrencyEnum.EUR),
                },
              )}
            >
              <Select value={periodDays} onChange={handleChangePeriodDays}>
                {[1, 3, 7, 14, 30, 3 * 30, 6 * 30, 12 * 30].map((days) => (
                  <option key={days} value={days}>
                    {days >= 30
                      ? t('month', { count: Math.ceil(days / 30) })
                      : t('days', { count: days })}
                  </option>
                ))}
              </Select>
            </FormItem>
          </Form>
        ) : (
          <Loader isWrapped />
        )}
      </ScrollView>
    </>
  );
};
